// src/lib/master-diagnostic-system.ts
import { DIAGNOSTIC_HELP_TEXTS } from "./diagnostic-help-texts";
export type DiagnosticDimension =
  | "DEMAND"
  | "WEBSITE"
  | "CONVERSION"
  | "SALES_QUALITY"
  | "PROCESS"
  | "POSITIONING"
  | "REP_PERFORMANCE";

export type MasterAnswerType = "number" | "scale" | "dropdown" | "boolean";

export type DiagnosticQuestion = {
  id: string;
  section:
    | "context"
    | "demand"
    | "website"
    | "sales"
    | "process"
    | "positioning"
    | "rep_performance";
  label: string;
  answerType: MasterAnswerType;
  weight: number; // 1-5
  dimensions: DiagnosticDimension[];
  options?: Array<{
    value: string;
    label: string;
    score: number; // 0-100
  }>;
  min?: number;
  max?: number;
  placeholder?: string;
  help?: string;
};

export type DimensionScore = {
  dimension: DiagnosticDimension;
  score: number; // 0-100
  weightedPoints: number;
  weightedMax: number;
  answeredQuestions: number;
};

export type DiagnosticScoringResult = {
  overallScore: number;
  dimensionScores: Record<DiagnosticDimension, DimensionScore>;
  strongestDimension: DiagnosticDimension;
  weakestDimension: DiagnosticDimension;
  weakestDimensionsRanked: DiagnosticDimension[];
  priorityDrivers: Array<{
    dimension: DiagnosticDimension;
    score: number;
    title: string;
    summary: string;
  }>;
  answerDiagnostics: Array<{
    questionId: string;
    label: string;
    normalizedScore: number;
    dimensions: DiagnosticDimension[];
    weight: number;
  }>;
};

const SCALE_OPTIONS_5 = [
  { value: "1", label: "Sehr schwach", score: 0 },
  { value: "2", label: "Eher schwach", score: 25 },
  { value: "3", label: "Teils / teils", score: 50 },
  { value: "4", label: "Gut", score: 75 },
  { value: "5", label: "Sehr gut", score: 100 },
];

const YES_NO_PARTLY = [
  { value: "yes", label: "Ja", score: 100 },
  { value: "partly", label: "Teilweise", score: 50 },
  { value: "no", label: "Nein", score: 0 },
  { value: "not-known", label: "Nicht bekannt", score: 35 },
];

const YES_NO = [
  { value: "yes", label: "Ja", score: 100 },
  { value: "no", label: "Nein", score: 0 },
  { value: "not-known", label: "Nicht bekannt", score: 35 },
];

export const MASTER_DIAGNOSTIC_QUESTIONS: DiagnosticQuestion[] = [
  // -------------------------------------------------
  // 1. Geschäftsmodell & Kontext (5)
  // -------------------------------------------------
  {
    id: "ctx-001",
    section: "context",
    label: "Wie erklärungsbedürftig ist Ihr Angebot?",
    help: DIAGNOSTIC_HELP_TEXTS["ctx-001"],
    answerType: "scale",
    weight: 3,
    dimensions: ["SALES_QUALITY"],
    options: [
      { value: "1", label: "Sehr einfach", score: 100 },
      { value: "2", label: "Eher einfach", score: 80 },
      { value: "3", label: "Mittel", score: 60 },
      { value: "4", label: "Komplex", score: 40 },
      { value: "5", label: "Sehr komplex", score: 20 },
    ],
  },
  {
    id: "ctx-002",
    section: "context",
    label: "Wie viele Entscheider sind typischerweise im Kaufprozess involviert?",
    help: DIAGNOSTIC_HELP_TEXTS["ctx-002"],
    answerType: "dropdown",
    weight: 3,
    dimensions: ["CONVERSION"],
    options: [
      { value: "1", label: "1 Entscheider", score: 100 },
      { value: "2-3", label: "2–3 Entscheider", score: 70 },
      { value: "4+", label: "4+ Entscheider", score: 40 },
      { value: "not-known", label: "Nicht bekannt", score: 35 },
    ],
  },
  {
    id: "ctx-003",
    section: "context",
    label: "Wie skalierbar ist Ihr Produkt / Ihre Dienstleistung?",
    help: DIAGNOSTIC_HELP_TEXTS["ctx-003"],
    answerType: "dropdown",
    weight: 4,
    dimensions: ["POSITIONING", "CONVERSION"],
    options: [
      { value: "highly-scalable", label: "Stark standardisiert / skalierbar", score: 100 },
      { value: "partly-scalable", label: "Teilweise standardisiert", score: 65 },
      { value: "project-heavy", label: "Stark individuell / projekthaft", score: 30 },
      { value: "not-known", label: "Nicht bekannt", score: 35 },
    ],
  },
  {
    id: "ctx-004",
    section: "context",
    label: "Wie klar ist Ihr Zielkunde definiert?",
    help: DIAGNOSTIC_HELP_TEXTS["ctx-004"],
    answerType: "scale",
    weight: 5,
    dimensions: ["POSITIONING"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "ctx-005",
    section: "context",
    label: "Wie standardisiert ist Ihr Verkaufsprozess aktuell?",
    help: DIAGNOSTIC_HELP_TEXTS["ctx-005"],
    answerType: "scale",
    weight: 4,
    dimensions: ["PROCESS"],
    options: SCALE_OPTIONS_5,
  },
  // -------------------------------------------------
  // 2. Nachfrage & Leads (7)
  // -------------------------------------------------
  {
    id: "dem-001",
    section: "demand",
    label: "Wie viele Leads generieren Sie monatlich?",
    help: DIAGNOSTIC_HELP_TEXTS["dem-001"],
    answerType: "number",
    weight: 5,
    dimensions: ["DEMAND"],
    min: 0,
    max: 10000,
  },
  {
    id: "dem-002",
    section: "demand",
    label: "Qualifizierungsrate (aus Audit berechnet)",
    help: DIAGNOSTIC_HELP_TEXTS["dem-002"],
    answerType: "scale",
    weight: 5,
    dimensions: ["DEMAND"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "dem-003",
    section: "demand",
    label: "Diversität der Lead-Quellen (aus Akquise-Mix berechnet)",
    help: DIAGNOSTIC_HELP_TEXTS["dem-003"],
    answerType: "scale",
    weight: 4,
    dimensions: ["DEMAND"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "dem-004",
    section: "demand",
    label: "Wie gut passen Leads zu Ihrem Angebot?",
    help: DIAGNOSTIC_HELP_TEXTS["dem-004"],
    answerType: "scale",
    weight: 4,
    dimensions: ["DEMAND", "POSITIONING"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "dem-005",
    section: "demand",
    label: "Was ist aktuell Ihre wichtigste Leadquelle?",
    help: DIAGNOSTIC_HELP_TEXTS["dem-005"],
    answerType: "dropdown",
    weight: 3,
    dimensions: ["DEMAND"],
    options: [
      { value: "referrals", label: "Empfehlungen", score: 90 },
      { value: "inbound", label: "Inbound / Website", score: 80 },
      { value: "outbound", label: "Outbound", score: 75 },
      { value: "partners", label: "Partner", score: 80 },
      { value: "paid", label: "Paid Ads", score: 65 },
      { value: "mixed", label: "Gemischt", score: 85 },
      { value: "unclear", label: "Unklar / nicht messbar", score: 30 },
    ],
  },
  {
    id: "dem-006",
    section: "demand",
    label: "Wie klar ist Ihr Ideal Customer Profile (ICP) definiert?",
    help: DIAGNOSTIC_HELP_TEXTS["dem-006"],
    answerType: "dropdown",
    weight: 5,
    dimensions: ["DEMAND", "POSITIONING"],
    options: [
      { value: "very-clear", label: "Sehr klar – dokumentiert und aktiv genutzt", score: 95 },
      { value: "mostly-clear", label: "Weitgehend klar – nicht formal dokumentiert", score: 75 },
      { value: "vague", label: "Eher vage – grobe Vorstellungen", score: 40 },
      { value: "not-defined", label: "Nicht definiert", score: 10 },
      { value: "not-known", label: "Nicht bekannt", score: 35 },
    ],
  },
  {
    id: "dem-007",
    section: "demand",
    label: "Wie stark ist Ihre Marktpräsenz in Ihrem Zielsegment?",
    help: DIAGNOSTIC_HELP_TEXTS["dem-007"],
    answerType: "dropdown",
    weight: 4,
    dimensions: ["DEMAND", "POSITIONING"],
    options: [
      { value: "strong", label: "Stark – im Markt gut bekannt", score: 90 },
      { value: "moderate", label: "Mittel – wahrgenommen, aber nicht erste Wahl", score: 60 },
      { value: "weak", label: "Schwach – kaum sichtbar", score: 25 },
      { value: "unknown", label: "Unklar – wissen wir nicht genau", score: 35 },
      { value: "not-known", label: "Nicht bekannt", score: 35 },
    ],
  },
  // -------------------------------------------------
  // 3. Website & Vertrauen (8)
  // -------------------------------------------------
  {
    id: "web-001",
    section: "website",
    label: "Versteht ein Besucher innerhalb von 5 Sekunden, was Sie anbieten?",
    help: DIAGNOSTIC_HELP_TEXTS["web-001"],
    answerType: "scale",
    weight: 5,
    dimensions: ["WEBSITE"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "web-002",
    section: "website",
    label: "Gibt es einen klaren Haupt-CTA auf der Website?",
    help: DIAGNOSTIC_HELP_TEXTS["web-002"],
    answerType: "boolean",
    weight: 5,
    dimensions: ["WEBSITE"],
    options: YES_NO,
  },
  {
    id: "web-003",
    section: "website",
    label: "Wie viele konkurrierende CTAs gibt es auf der Startseite?",
    help: DIAGNOSTIC_HELP_TEXTS["web-003"],
    answerType: "dropdown",
    weight: 3,
    dimensions: ["WEBSITE"],
    options: [
      { value: "1", label: "1 klarer CTA", score: 100 },
      { value: "2-3", label: "2–3 CTAs", score: 70 },
      { value: "4+", label: "4 oder mehr", score: 30 },
      { value: "not-known", label: "Nicht bekannt", score: 35 },
    ],
  },
  {
    id: "web-004",
    section: "website",
    label: "Gibt es sichtbare Testimonials?",
    help: DIAGNOSTIC_HELP_TEXTS["web-004"],
    answerType: "boolean",
    weight: 4,
    dimensions: ["WEBSITE"],
    options: YES_NO,
  },
  {
    id: "web-005",
    section: "website",
    label: "Gibt es konkrete Case Studies oder nachweisbare Ergebnisse?",
    help: DIAGNOSTIC_HELP_TEXTS["web-005"],
    answerType: "boolean",
    weight: 5,
    dimensions: ["WEBSITE", "POSITIONING"],
    options: YES_NO,
  },
  {
    id: "web-006",
    section: "website",
    label: "Wird der Nutzen klarer kommuniziert als die Leistung?",
    help: DIAGNOSTIC_HELP_TEXTS["web-006"],
    answerType: "scale",
    weight: 5,
    dimensions: ["POSITIONING"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "web-007",
    section: "website",
    label: "Wie stark differenziert sich Ihre Website von Wettbewerbern?",
    help: DIAGNOSTIC_HELP_TEXTS["web-007"],
    answerType: "scale",
    weight: 4,
    dimensions: ["POSITIONING", "WEBSITE"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "web-008",
    section: "website",
    label: "Wie hoch ist das Vertrauen, das Ihre Website aktuell auslöst?",
    help: DIAGNOSTIC_HELP_TEXTS["web-008"],
    answerType: "scale",
    weight: 5,
    dimensions: ["WEBSITE"],
    options: SCALE_OPTIONS_5,
  },
  // -------------------------------------------------
  // 4. Sales-Prozess & Conversion (9)
  // -------------------------------------------------
  {
    id: "sal-001",
    section: "sales",
    label: "Wie viele Erstgespräche führen Sie monatlich?",
    help: DIAGNOSTIC_HELP_TEXTS["sal-001"],
    answerType: "number",
    weight: 5,
    dimensions: ["CONVERSION"],
    min: 0,
    max: 5000,
  },
  {
    id: "sal-002",
    section: "sales",
    label: "Wie hoch ist die Conversion von Gespräch zu echter Opportunity?",
    help: DIAGNOSTIC_HELP_TEXTS["sal-002"],
    answerType: "number",
    weight: 5,
    dimensions: ["SALES_QUALITY"],
    min: 0,
    max: 100,
  },
  {
    id: "sal-003",
    section: "sales",
    label: "Wie viele Angebote erstellen Sie monatlich?",
    help: DIAGNOSTIC_HELP_TEXTS["sal-003"],
    answerType: "number",
    weight: 5,
    dimensions: ["CONVERSION"],
    min: 0,
    max: 5000,
  },
  {
    id: "sal-004",
    section: "sales",
    label: "Gibt es ein standardisiertes Discovery Framework?",
    help: DIAGNOSTIC_HELP_TEXTS["sal-004"],
    answerType: "boolean",
    weight: 5,
    dimensions: ["SALES_QUALITY"],
    options: YES_NO_PARTLY,
  },
  {
    id: "sal-005",
    section: "sales",
    label: "Werden im Gespräch immer dieselben Kernfragen gestellt?",
    help: DIAGNOSTIC_HELP_TEXTS["sal-005"],
    answerType: "scale",
    weight: 4,
    dimensions: ["SALES_QUALITY"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "sal-006",
    section: "sales",
    label: "Wann wird typischerweise ein Angebot erstellt?",
    help: DIAGNOSTIC_HELP_TEXTS["sal-006"],
    answerType: "dropdown",
    weight: 4,
    dimensions: ["SALES_QUALITY"],
    options: [
      { value: "after-clear-qualification", label: "Erst nach klarer Qualifizierung", score: 100 },
      { value: "after-first-call", label: "Oft direkt nach dem Erstgespräch", score: 45 },
      { value: "very-early", label: "Sehr früh / oft vorschnell", score: 10 },
      { value: "not-known", label: "Nicht bekannt", score: 35 },
    ],
  },
  {
    id: "sal-007",
    section: "sales",
    label: "Wie strukturiert sind Ihre Gespräche aktuell?",
    help: DIAGNOSTIC_HELP_TEXTS["sal-007"],
    answerType: "scale",
    weight: 4,
    dimensions: ["SALES_QUALITY"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "sal-008",
    section: "sales",
    label: "Versteht der Kunde nach dem Gespräch den konkreten Mehrwert?",
    help: DIAGNOSTIC_HELP_TEXTS["sal-008"],
    answerType: "scale",
    weight: 5,
    dimensions: ["POSITIONING", "SALES_QUALITY"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "sal-009",
    section: "sales",
    label: "Wie früh gelingt es Ihnen, alle relevanten Entscheider in den Prozess einzubinden?",
    help: DIAGNOSTIC_HELP_TEXTS["sal-009"],
    answerType: "dropdown",
    weight: 5,
    dimensions: ["CONVERSION", "SALES_QUALITY"],
    options: [
      { value: "very-early", label: "Sehr früh (vor oder im ersten Gespräch)", score: 100 },
      { value: "during-process", label: "Im Verlauf des Prozesses", score: 70 },
      { value: "late", label: "Erst kurz vor Abschluss", score: 25 },
      { value: "rarely", label: "Selten / gar nicht", score: 0 },
    ],
  },
  // -------------------------------------------------
  // 5. Ownership & Steuerung (9)
  // -------------------------------------------------
  {
    id: "pro-001",
    section: "process",
    label: "Hat jeder neue Lead einen klaren Owner?",
    help: DIAGNOSTIC_HELP_TEXTS["pro-001"],
    answerType: "boolean",
    weight: 5,
    dimensions: ["PROCESS"],
    options: YES_NO_PARTLY,
  },
  {
    id: "pro-002",
    section: "process",
    label: "Gibt es verbindliche Reaktionsstandards?",
    help: DIAGNOSTIC_HELP_TEXTS["pro-002"],
    answerType: "boolean",
    weight: 5,
    dimensions: ["PROCESS"],
    options: YES_NO_PARTLY,
  },
  {
    id: "pro-003",
    section: "process",
    label: "Gibt es nach jedem Gespräch einen klar definierten Next Step?",
    help: DIAGNOSTIC_HELP_TEXTS["pro-003"],
    answerType: "scale",
    weight: 5,
    dimensions: ["PROCESS"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "pro-004",
    section: "process",
    label: "Gibt es definierte Follow-up-Prozesse?",
    help: DIAGNOSTIC_HELP_TEXTS["pro-004"],
    answerType: "boolean",
    weight: 5,
    dimensions: ["PROCESS"],
    options: YES_NO_PARTLY,
  },
  {
    id: "pro-005",
    section: "process",
    label: "Wie konsequent wird nach Angeboten nachgefasst?",
    help: DIAGNOSTIC_HELP_TEXTS["pro-005"],
    answerType: "scale",
    weight: 5,
    dimensions: ["PROCESS"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "pro-006",
    section: "process",
    label: "Gibt es regelmäßige Pipeline-Reviews?",
    help: DIAGNOSTIC_HELP_TEXTS["pro-006"],
    answerType: "boolean",
    weight: 4,
    dimensions: ["PROCESS"],
    options: YES_NO_PARTLY,
  },
  {
    id: "pro-007",
    section: "process",
    label: "Wie transparent und belastbar ist Ihr Forecast?",
    help: DIAGNOSTIC_HELP_TEXTS["pro-007"],
    answerType: "scale",
    weight: 4,
    dimensions: ["PROCESS"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "pro-008",
    section: "process",
    label: "Wie konsequent wird Ihr CRM im Alltag genutzt?",
    help: DIAGNOSTIC_HELP_TEXTS["pro-008"],
    answerType: "scale",
    weight: 5,
    dimensions: ["PROCESS"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "pro-009",
    section: "process",
    label: "Hängt Ihr Vertriebserfolg stark von einzelnen Personen ab?",
    help: DIAGNOSTIC_HELP_TEXTS["pro-009"],
    answerType: "scale",
    weight: 5,
    dimensions: ["PROCESS"],
    options: [
      { value: "1", label: "Überhaupt nicht", score: 100 },
      { value: "2", label: "Kaum", score: 75 },
      { value: "3", label: "Teilweise", score: 50 },
      { value: "4", label: "Stark", score: 25 },
      { value: "5", label: "Sehr stark", score: 0 },
    ],
  },
  // -------------------------------------------------
  // 6. Markt & Positionierung (9)
  // -------------------------------------------------
  {
    id: "pos-001",
    section: "positioning",
    label: "Wie klar ist Ihre Differenzierung im Markt?",
    help: DIAGNOSTIC_HELP_TEXTS["pos-001"],
    answerType: "scale",
    weight: 5,
    dimensions: ["POSITIONING"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "pos-002",
    section: "positioning",
    label: "Wie häufig werden Sie primär über den Preis verglichen?",
    help: DIAGNOSTIC_HELP_TEXTS["pos-002"],
    answerType: "scale",
    weight: 5,
    dimensions: ["POSITIONING"],
    options: [
      { value: "1", label: "Fast nie", score: 100 },
      { value: "2", label: "Selten", score: 75 },
      { value: "3", label: "Teilweise", score: 50 },
      { value: "4", label: "Oft", score: 25 },
      { value: "5", label: "Sehr oft", score: 0 },
    ],
  },
  {
    id: "pos-003",
    section: "positioning",
    label: "Können Sie Ihren Mehrwert in einem Satz klar erklären?",
    help: DIAGNOSTIC_HELP_TEXTS["pos-003"],
    answerType: "scale",
    weight: 5,
    dimensions: ["POSITIONING"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "pos-004",
    section: "positioning",
    label: "Verkaufen Sie aktuell eher über Nutzen oder über Features?",
    help: DIAGNOSTIC_HELP_TEXTS["pos-004"],
    answerType: "dropdown",
    weight: 4,
    dimensions: ["POSITIONING"],
    options: [
      { value: "outcomes", label: "Primär über Geschäftsnutzen / Outcomes", score: 100 },
      { value: "mixed", label: "Gemischt", score: 60 },
      { value: "features", label: "Primär über Features / Leistungen", score: 20 },
    ],
  },
  {
    id: "pos-005",
    section: "positioning",
    label: "Gibt es ein klar definiertes ICP?",
    help: DIAGNOSTIC_HELP_TEXTS["pos-005"],
    answerType: "boolean",
    weight: 5,
    dimensions: ["POSITIONING"],
    options: YES_NO_PARTLY,
  },
  {
    id: "pos-006",
    section: "positioning",
    label: "Wie gut passt Ihre aktuelle Zielgruppe zu Ihrem Angebot?",
    help: DIAGNOSTIC_HELP_TEXTS["pos-006"],
    answerType: "scale",
    weight: 5,
    dimensions: ["POSITIONING"],
    options: SCALE_OPTIONS_5,
  },
  {
    id: "pos-007",
    section: "positioning",
    label: "Wie stark ist der Wettbewerbsdruck in Ihrem Markt?",
    help: DIAGNOSTIC_HELP_TEXTS["pos-007"],
    answerType: "scale",
    weight: 3,
    dimensions: ["POSITIONING"],
    options: [
      { value: "1", label: "Sehr gering", score: 100 },
      { value: "2", label: "Eher gering", score: 75 },
      { value: "3", label: "Mittel", score: 50 },
      { value: "4", label: "Hoch", score: 30 },
      { value: "5", label: "Sehr hoch", score: 15 },
    ],
  },
  {
    id: "pos-008",
    section: "positioning",
    label: "Gibt es harte Proof-Punkte wie Zahlen, Ergebnisse oder Referenzen?",
    help: DIAGNOSTIC_HELP_TEXTS["pos-008"],
    answerType: "boolean",
    weight: 5,
    dimensions: ["POSITIONING", "WEBSITE"],
    options: YES_NO_PARTLY,
  },
  {
    id: "pos-009",
    section: "positioning",
    label: "Wie überzeugend ist Ihr Gesamtauftritt im Vergleich zum Wettbewerb?",
    help: DIAGNOSTIC_HELP_TEXTS["pos-009"],
    answerType: "scale",
    weight: 4,
    dimensions: ["POSITIONING"],
    options: SCALE_OPTIONS_5,
  },
  
  // -------------------------------------------------
  // 7. Rep Performance & Zielerreichung (10)
  // -------------------------------------------------
  {
    id: "rep-001",
    section: "rep_performance",
    label: "Wie viele produktive Sales Reps haben Sie aktuell?",
    help: DIAGNOSTIC_HELP_TEXTS["rep-001"],
    answerType: "number",
    weight: 3,
    dimensions: ["REP_PERFORMANCE"],
    min: 0,
    max: 100,
    placeholder: "z.B. 5",
  },
  // rep-002 and rep-003 removed - replaced by rep-011, rep-012, rep-013 (v2 Pace & Forecast)
  {
    id: "rep-002",
    section: "rep_performance",
    label: "Wie viele Ihrer Reps erreichen ihr Ziel regelmäßig?",
    help: DIAGNOSTIC_HELP_TEXTS["rep-002"],
    answerType: "dropdown",
    weight: 5,
    dimensions: ["REP_PERFORMANCE"],
    options: [
      { value: "almost-all", label: "Fast alle", score: 100 },
      { value: "more-than-half", label: "Mehr als die Hälfte", score: 70 },
      { value: "less-than-half", label: "Weniger als die Hälfte", score: 30 },
      { value: "hardly-any", label: "Kaum jemand", score: 10 },
      { value: "not-known", label: "Nicht bekannt", score: 35 },
    ],
  },
  {
    id: "rep-003",
    section: "rep_performance",
    label: "Wie stark unterscheidet sich die Leistung Ihrer Reps?",
    help: DIAGNOSTIC_HELP_TEXTS["rep-003"],
    answerType: "dropdown",
    weight: 4,
    dimensions: ["REP_PERFORMANCE", "PROCESS"],
    options: [
      { value: "low", label: "Gering - alle ähnlich", score: 100 },
      { value: "moderate", label: "Moderat", score: 70 },
      { value: "high", label: "Groß", score: 30 },
      { value: "very-high", label: "Sehr groß", score: 10 },
      { value: "not-known", label: "Nicht bekannt", score: 35 },
    ],
  },
  {
    id: "rep-004",
    section: "rep_performance",
    label: "Durchschnittliche offene Pipeline pro Rep",
    help: DIAGNOSTIC_HELP_TEXTS["rep-004"],
    answerType: "number",
    weight: 4,
    dimensions: ["REP_PERFORMANCE"],
    min: 0,
    max: 10000000,
    placeholder: "z.B. 180000",
  },
  {
    id: "rep-005",
    section: "rep_performance",
    label: "Wie viel Prozent der Pipeline sind realistisch abschließbar?",
    help: DIAGNOSTIC_HELP_TEXTS["rep-005"],
    answerType: "number",
    weight: 4,
    dimensions: ["REP_PERFORMANCE", "SALES_QUALITY"],
    min: 0,
    max: 100,
    placeholder: "z.B. 35",
  },
  {
    id: "rep-006",
    section: "rep_performance",
    label: "Wie viel der Pipeline liegt zu lange ohne echten Fortschritt offen?",
    help: DIAGNOSTIC_HELP_TEXTS["rep-006"],
    answerType: "dropdown",
    weight: 4,
    dimensions: ["REP_PERFORMANCE", "PROCESS"],
    options: [
      { value: "under-10", label: "Unter 10%", score: 100 },
      { value: "10-25", label: "10-25%", score: 70 },
      { value: "25-50", label: "25-50%", score: 30 },
      { value: "over-50", label: "Über 50%", score: 10 },
      { value: "not-known", label: "Nicht bekannt", score: 35 },
    ],
  },
  {
    id: "rep-007",
    section: "rep_performance",
    label: "Gibt es klare Kriterien dafür, wann eine Opportunity als valide Pipeline zählt?",
    help: DIAGNOSTIC_HELP_TEXTS["rep-007"],
    answerType: "dropdown",
    weight: 4,
    dimensions: ["REP_PERFORMANCE", "SALES_QUALITY", "PROCESS"],
    options: YES_NO_PARTLY,
  },
  {
    id: "rep-008",
    section: "rep_performance",
    label: "Wie regelmäßig wird die Performance einzelner Reps systematisch reviewed?",
    help: DIAGNOSTIC_HELP_TEXTS["rep-008"],
    answerType: "dropdown",
    weight: 4,
    dimensions: ["REP_PERFORMANCE", "PROCESS"],
    options: [
      { value: "weekly", label: "Wöchentlich", score: 100 },
      { value: "biweekly", label: "Alle 2 Wochen", score: 80 },
      { value: "monthly", label: "Monatlich", score: 60 },
      { value: "rarely", label: "Selten", score: 30 },
      { value: "never", label: "Nie", score: 0 },
      { value: "not-known", label: "Nicht bekannt", score: 35 },
    ],
  },
  // NEW: Rep Performance v2 - Pace & Forecast Questions
  {
    id: "rep-009",
    section: "rep_performance",
    label: "Wie hoch ist Ihr Umsatzziel für das gesamte Jahr?",
    help: "Das Gesamtumsatzziel Ihres Vertriebsteams für das laufende Kalenderjahr.",
    answerType: "number",
    weight: 5,
    dimensions: ["REP_PERFORMANCE"],
    min: 0,
    max: 100000000,
    placeholder: "z.B. 2400000",
  },
  {
    id: "rep-010",
    section: "rep_performance",
    label: "Wie viel Umsatz haben Sie bisher in diesem Jahr erzielt (YTD)?",
    help: "Der kumulierte Umsatz seit Jahresbeginn bis heute.",
    answerType: "number",
    weight: 5,
    dimensions: ["REP_PERFORMANCE"],
    min: 0,
    max: 100000000,
    placeholder: "z.B. 800000",
  },
  {
    id: "rep-011",
    section: "rep_performance",
    label: "Ist das Umsatzziel auf einzelne Reps heruntergebrochen?",
    help: "Haben Ihre Reps individuelle, verbindliche Umsatzziele?",
    answerType: "dropdown",
    weight: 4,
    dimensions: ["REP_PERFORMANCE"],
    options: [
      { value: "yes", label: "Ja, klar definiert", score: 100 },
      { value: "partial", label: "Teilweise", score: 60 },
      { value: "no", label: "Nein", score: 20 },
      { value: "not-known", label: "Nicht bekannt", score: 40 },
    ],
  },
  {
    id: "rep-012",
    section: "rep_performance",
    label: "Wie viele Ihrer Reps verfügen über ausreichend Pipeline für ihre Ziele?",
    help: "Anteil der Reps mit genug qualifizierter Pipeline, um ihre Ziele realistisch zu erreichen.",
    answerType: "dropdown",
    weight: 4,
    dimensions: ["REP_PERFORMANCE"],
    options: [
      { value: "almost-all", label: "Fast alle", score: 100 },
      { value: "more-than-half", label: "Mehr als die Hälfte", score: 65 },
      { value: "less-than-half", label: "Weniger als die Hälfte", score: 35 },
      { value: "hardly-any", label: "Kaum jemand", score: 10 },
      { value: "not-known", label: "Nicht bekannt", score: 40 },
    ],
  },
  {
    id: "rep-013",
    section: "rep_performance",
    label: "Wie zuversichtlich sind Sie, dass die aktuelle Pipeline zu Abschlüssen führt?",
    help: "Ihre Einschätzung der Qualität und Abschlusswahrscheinlichkeit der Pipeline.",
    answerType: "dropdown",
    weight: 4,
    dimensions: ["REP_PERFORMANCE"],
    options: [
      { value: "very-high", label: "Sehr hoch", score: 100 },
      { value: "rather-high", label: "Eher hoch", score: 70 },
      { value: "rather-low", label: "Eher niedrig", score: 40 },
      { value: "very-low", label: "Sehr niedrig", score: 15 },
      { value: "not-known", label: "Nicht bekannt", score: 45 },
    ],
  },
  {
    id: "rep-014",
    section: "rep_performance",
    label: "Wie oft verlieren Deals an Momentum oder stagnieren?",
    help: "Häufigkeit, mit der Opportunities ohne klaren Grund ins Stocken geraten.",
    answerType: "dropdown",
    weight: 3,
    dimensions: ["REP_PERFORMANCE", "PROCESS"],
    options: [
      { value: "rarely", label: "Selten", score: 100 },
      { value: "sometimes", label: "Manchmal", score: 70 },
      { value: "often", label: "Oft", score: 40 },
      { value: "very-often", label: "Sehr oft", score: 15 },
      { value: "not-known", label: "Nicht bekannt", score: 50 },
    ],
  },
];


export const DIMENSION_LABELS: Record<DiagnosticDimension, string> = {
  DEMAND: "Nachfrage & Leadqualität",
  WEBSITE: "Inbound-Prozess & Qualifizierung",
  CONVERSION: "Funnel & Conversion",
  SALES_QUALITY: "Sales-Qualität & Discovery",
  PROCESS: "Prozess, Ownership & Steuerung",
  POSITIONING: "Positionierung & Differenzierung",
  REP_PERFORMANCE: "Rep Performance & Zielerreichung",
};

export function getDiagnosticQuestionsBySection(
  section: DiagnosticQuestion["section"]
) {
  return MASTER_DIAGNOSTIC_QUESTIONS.filter((q) => q.section === section);
}

function scoreFromScale(value: unknown): number {
  const str = String(value ?? "").trim();
  const match = SCALE_OPTIONS_5.find((o) => o.value === str);
  return match ? match.score : 35;
}

function scoreFromBoolean(question: DiagnosticQuestion, value: unknown): number {
  const str = String(value ?? "").trim();
  const match = question.options?.find((o) => o.value === str);
  return match ? match.score : 35;
}

function scoreFromDropdown(question: DiagnosticQuestion, value: unknown): number {
  const str = String(value ?? "").trim();
  const match = question.options?.find((o) => o.value === str);
  return match ? match.score : 35;
}

function scoreFromNumber(question: DiagnosticQuestion, answers: Record<string, unknown>): number {
  const raw = answers[question.id];
  const n = Number(raw);
  if (!Number.isFinite(n)) return 35;

  // Individuelle Logik pro Zahlenfrage
  switch (question.id) {
    case "dem-001": // Leads / Monat
      if (n >= 150) return 90;
      if (n >= 75) return 75;
      if (n >= 30) return 55;
      if (n >= 10) return 35;
      return 15;

    case "dem-002": { // Qualifizierte Leads / Monat
      const totalLeads = Number(answers["dem-001"]);
      if (Number.isFinite(totalLeads) && totalLeads > 0) {
        const ratio = (n / totalLeads) * 100;
        if (ratio >= 60) return 95;
        if (ratio >= 40) return 75;
        if (ratio >= 25) return 50;
        if (ratio >= 10) return 25;
        return 10;
      }
      if (n >= 50) return 85;
      if (n >= 20) return 65;
      if (n >= 10) return 45;
      return 20;
    }

    case "sal-001": // Erstgespräche
      if (n >= 25) return 90;
      if (n >= 12) return 70;
      if (n >= 5) return 50;
      return 20;

    case "sal-002": // Gespräch -> Opportunity %
      if (n >= 60) return 95;
      if (n >= 40) return 75;
      if (n >= 25) return 50;
      if (n >= 15) return 30;
      return 10;

    case "sal-003": // Angebote
      if (n >= 15) return 90;
      if (n >= 8) return 70;
      if (n >= 4) return 50;
      return 20;

    default:
      if (n <= 0) return 20;
      return 60;
  }
}

function scoreQuestion(
  question: DiagnosticQuestion,
  answers: Record<string, unknown>
): number {
  if (answers[question.id] === undefined || answers[question.id] === null || answers[question.id] === "") {
    return 35;
  }

  switch (question.answerType) {
    case "scale":
      return scoreFromScale(answers[question.id]);
    case "boolean":
      return scoreFromBoolean(question, answers[question.id]);
    case "dropdown":
      return scoreFromDropdown(question, answers[question.id]);
    case "number":
      return scoreFromNumber(question, answers);
    default:
      return 35;
  }
}

export function calculateDiagnosticScoring(
  answers: Record<string, unknown>
): DiagnosticScoringResult {
  const dimensionBuckets: Record<DiagnosticDimension, { points: number; max: number; answered: number }> = {
    DEMAND: { points: 0, max: 0, answered: 0 },
    WEBSITE: { points: 0, max: 0, answered: 0 },
    CONVERSION: { points: 0, max: 0, answered: 0 },
    SALES_QUALITY: { points: 0, max: 0, answered: 0 },
    PROCESS: { points: 0, max: 0, answered: 0 },
    POSITIONING: { points: 0, max: 0, answered: 0 },
    REP_PERFORMANCE: { points: 0, max: 0, answered: 0 },
  };

  const answerDiagnostics = MASTER_DIAGNOSTIC_QUESTIONS.map((question) => {
    const normalizedScore = scoreQuestion(question, answers);

    for (const dimension of question.dimensions) {
      dimensionBuckets[dimension].points += normalizedScore * question.weight;
      dimensionBuckets[dimension].max += 100 * question.weight;
      dimensionBuckets[dimension].answered += 1;
    }

    return {
      questionId: question.id,
      label: question.label,
      normalizedScore,
      dimensions: question.dimensions,
      weight: question.weight,
    };
  });

  const dimensionScores = Object.fromEntries(
    (Object.keys(dimensionBuckets) as DiagnosticDimension[]).map((dimension) => {
      const bucket = dimensionBuckets[dimension];
      const score = bucket.max > 0 ? Math.round((bucket.points / bucket.max) * 100) : 0;
      return [
        dimension,
        {
          dimension,
          score,
          weightedPoints: bucket.points,
          weightedMax: bucket.max,
          answeredQuestions: bucket.answered,
        },
      ];
    })
  ) as Record<DiagnosticDimension, DimensionScore>;

  const ranked = (Object.values(dimensionScores) as DimensionScore[])
    .sort((a, b) => a.score - b.score);

  const weakestDimension = ranked[0]?.dimension || "PROCESS";
  const strongestDimension = ranked[ranked.length - 1]?.dimension || "POSITIONING";

  const overallScore = Math.round(
    ranked.reduce((sum, item) => sum + item.score, 0) / Math.max(ranked.length, 1)
  );

  const priorityDrivers = ranked.slice(0, 3).map((item) => ({
    dimension: item.dimension,
    score: item.score,
    title: DIMENSION_LABELS[item.dimension],
    summary:
      item.dimension === "WEBSITE"
        ? "Der Inbound-Qualifizierungsprozess ist nicht sauber strukturiert — Kontakte werden zu langsam oder zu uneinheitlich in den Sales-Prozess überführt."
        : item.dimension === "DEMAND"
        ? "Leadqualität, Quellenmix oder Vorqualifizierung limitieren die Pipeline vor dem Gespräch."
        : item.dimension === "CONVERSION"
        ? "Im Funnel entstehen messbare Reibungsverluste zwischen Kontakt, Gespräch und Opportunity."
        : item.dimension === "SALES_QUALITY"
        ? "Discovery, Bedarfstiefe oder Gesprächsführung verhindern reife Opportunities."
        : item.dimension === "PROCESS"
        ? "Ownership, Standards und Steuerung sind nicht stark genug verankert."
        : item.dimension === "REP_PERFORMANCE"
        ? "Zielerreichung, Leistungsstreuung oder Pipeline-Qualität pro Rep limitieren den Umsatz."
        : "Positionierung, Proof oder Differenzierung schwächen Vertrauen und Kaufreife.",
  }));

  return {
    overallScore,
    dimensionScores,
    strongestDimension,
    weakestDimension,
    weakestDimensionsRanked: ranked.map((r) => r.dimension),
    priorityDrivers,
    answerDiagnostics,
  };
}

export function mapWeakestDimensionsToPriorityTitles(
  dimensions: DiagnosticDimension[]
): string[] {
  return dimensions.slice(0, 3).map((dimension) => {
    switch (dimension) {
      case "WEBSITE":
        return "Inbound-Qualifizierungsprozess und Kontakt-zu-Discovery-Rate verbessern";
      case "DEMAND":
        return "Leadqualität und Nachfrage-Fit verbessern";
      case "CONVERSION":
        return "Funnel-Leckagen zwischen Kontakt und Opportunity schließen";
      case "SALES_QUALITY":
        return "Discovery und Gesprächsqualität schärfen";
      case "PROCESS":
        return "Ownership, Follow-up und Pipeline-Steuerung stabilisieren";
      case "POSITIONING":
        return "Positionierung und Differenzierung klarer machen";
      case "REP_PERFORMANCE":
        return "Rep-Performance, Zielerreichung und Pipeline-Qualität verbessern";
      default:
        return "Priorität schärfen";
    }
  });
}